import { staffEntryConsentDate } from '@steward-app/steward-types';

/**
 * Which of the client's answers their advisor entered (docs/43 §3.3, §4; crossmarc/steward-app#2538),
 * read off the provenance the client's own read carries (`via`, `at`, never `by`). Shared by the
 * snapshot wizard and the onboarding form, and by the firm's staff form for its hidden markers'
 * labels.
 *
 * The mark is DERIVED, as on the server: an answer is marked while its stored `via` is `staff`. A
 * save that changes the value takes the client's provenance (the server's only-on-change rule), so
 * a page that saved a changed value drops that mark at once instead of waiting for its next read
 * ({@link dropChanged}).
 */

/** Unified id (a scalar) or `listId#itemId` (a list item with a staff field) to the newest staff `at`. */
export type StaffMarks = ReadonlyMap<string, string>;

const viaOf = (a: unknown): { via?: unknown; at?: unknown } =>
  (typeof a === 'object' && a !== null ? a as { via?: unknown; at?: unknown } : {});

export function staffMarksOf(view: {
  answers?: Record<string, unknown>;
  lists?: Record<string, { id: string; fields: Record<string, unknown> }[]>;
} | undefined): Map<string, string> {
  const out = new Map<string, string>();
  if (!view) return out;
  const put = (key: string, at: string): void => { const was = out.get(key); if (was === undefined || at > was) out.set(key, at); };
  for (const [id, a] of Object.entries(view.answers ?? {})) {
    const { via, at } = viaOf(a);
    if (via === 'staff' && typeof at === 'string') put(id, at);
  }
  for (const [listId, items] of Object.entries(view.lists ?? {})) {
    if (!Array.isArray(items)) continue;
    for (const item of items) {
      for (const a of Object.values(item?.fields ?? {})) {
        const { via, at } = viaOf(a);
        if (via === 'staff' && typeof at === 'string') put(`${listId}#${item.id}`, at);
      }
    }
  }
  return out;
}

/** The earliest staff `at` among the marks, or null: the date the notice names. */
export function earliestStaffAt(marks: StaffMarks): string | null {
  let first: string | null = null;
  for (const at of marks.values()) if (first === null || at < first) first = at;
  return first;
}

/** A mark's date as the marks print it (the consent sentence's own UTC calendar date). */
export function markDate(at: string): string {
  try { return staffEntryConsentDate(at); } catch { return ''; }
}

/** The marks after a save that sent `after` over `before`: an answer whose value changed is the client's now. */
export function dropChanged(
  marks: StaffMarks,
  before: { answers: Record<string, unknown>; lists: Record<string, { id: string; fields: Record<string, unknown> }[]> },
  after: { answers: Record<string, unknown>; lists: Record<string, { id: string; fields: Record<string, unknown> }[]> },
): Map<string, string> {
  const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
  const out = new Map(marks);
  for (const key of marks.keys()) {
    const hash = key.indexOf('#');
    if (hash < 0) {
      if (!same(before.answers[key], after.answers[key])) out.delete(key);
      continue;
    }
    const listId = key.slice(0, hash);
    const itemId = key.slice(hash + 1);
    const b = before.lists[listId]?.find((i) => i.id === itemId);
    const a = after.lists[listId]?.find((i) => i.id === itemId);
    if (!same(b?.fields, a?.fields)) out.delete(key);
  }
  return out;
}

/**
 * A plain label for a question id when the form's own label is not to hand: the last segment of
 * the id, split at its capitals, sentence case ("aboutYou.firstName" reads "First name").
 */
export function questionLabel(id: string): string {
  const base = id.split('#')[0]!;
  const last = base.split('.').pop()!.replace('[]', '');
  const words = last.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The snapshot section a mark belongs to: the first segment of its id. */
export const sectionOfMark = (key: string): string => key.split('#')[0]!.split('.')[0]!.replace('[]', '');
